import { Box, Button, Modal, Title, UnstyledButton } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router-dom';

import { closeIcon } from '@assets';
import { classes } from '@styles';

interface LeavePageNotificationProps {
  hasUnsavedChanges: boolean;
  ignoreSearchParams?: boolean;
  ignoreIdAddedToPath?: boolean;
  onContinue?: () => void;
  onCancel?: () => void;
}

// eslint-disable-next-line max-statements
const isOnlyAddReplacedByMongoId = (oldPath: string, newPath: string) => {
  const mongoIdRegex = /^[a-f\d]{24}$/i;

  const oldSegments = oldPath.split('/');
  const newSegments = newPath.split('/');

  if (oldSegments.length !== newSegments.length) return false;

  let changedCount = 0;

  for (let i = 0; i < oldSegments.length; i++) {
    if (oldSegments[i] !== newSegments[i]) {
      changedCount++;

      if (changedCount > 1 || oldSegments[i] !== 'add' || !mongoIdRegex.test(newSegments[i])) {
        return false;
      }
    }
  }

  return changedCount === 1;
};

export const LeavePageNotification: React.FC<LeavePageNotificationProps> = ({
  hasUnsavedChanges,
  ignoreSearchParams,
  ignoreIdAddedToPath,
  onContinue,
  onCancel,
}) => {
  const [showSaveModal, setShowSaveModal] = useState(false);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (ignoreSearchParams && currentLocation.pathname === nextLocation.pathname) {
      return false;
    }

    if (ignoreIdAddedToPath && isOnlyAddReplacedByMongoId(currentLocation.pathname, nextLocation.pathname)) {
      return false;
    }

    return hasUnsavedChanges;
  });

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowSaveModal(true);
    }
  }, [blocker, hasUnsavedChanges]);

  useBeforeUnload((event) => {
    if (hasUnsavedChanges) {
      event.preventDefault();
    }
  });

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    blocker.reset();
    setShowSaveModal(false);
  };

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
    }
    blocker.proceed();
    setShowSaveModal(false);
  };

  return (
    <>
      {showSaveModal && (
        <Modal opened={showSaveModal} onClose={handleCancel} withCloseButton={false}>
          <Box className={classes.header}>
            <Title></Title>
            <div>
              <UnstyledButton onClick={handleCancel}>
                <img src={closeIcon} alt="Close" />
              </UnstyledButton>
            </div>
          </Box>

          <Box p={15}>
            <Box h={200} p={55} ta="center">
              You have unsaved changes. Would you like to continue without saving?
            </Box>
          </Box>

          <Box className={classes.footer}>
            <Button onClick={handleCancel} size="lg" variant="transparent">
              Cancel
            </Button>
            <Button onClick={handleContinue} variant="filled" size="lg">
              Continue
            </Button>
          </Box>
        </Modal>
      )}
    </>
  );
};
